import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type {
  SheetAnswer, SheetConfirmConflict, SheetRow, StudentListRow,
} from '../../../api/types';
import {
  useSheet, useSheetConfirm, useSheetPatch, useSheetReread, useSheetsWritten, useStudentsList,
} from '../../../api/hooks';
import { Button, ConfirmDialog, ErrorPanel, RadioCards, ShowMoreFooter, Spinner, Term, TextLinkButton } from '../../../components/ui';
import { apiErrorText } from '../../../lib/apiError';
import { personName } from '../../../lib/names';
import { useBlobUrl } from './useBlobUrl';
import { warningLabel } from './scanWarnings';
import AnswerStrip, { LOW_CONFIDENCE } from './AnswerStrip';
import SheetOverlay from './SheetOverlay';
import CornerPicker from './CornerPicker';
import { flaggedRows } from '../sheetStats';
import { formatRoll } from '../../../lib/roll';

// Answers are typed one keystroke / arrow-key at a time (via AnswerStrip), so
// they're batched into one PATCH after this pause (plan B Task 6). Student
// and discard are single, deliberate actions and go out immediately.
const ANSWERS_DEBOUNCE_MS = 400;
/** Students listed in the picker before "Show all". */
const STUDENT_PREVIEW = 20;
/** Sheet zoom steps: fit the column, or larger to tell close marks apart. */
const ZOOMS = [1, 1.5, 2] as const;

/** The 409 body from `POST sheets/<id>/confirm/` ({detail, code, needs_replace}), or null. */
function extractConflict(err: unknown): SheetConfirmConflict | null {
  const e = err as { response?: { status?: number; data?: unknown } } | null | undefined;
  if (e?.response?.status !== 409) return null;
  const data = e.response?.data as Partial<SheetConfirmConflict> | undefined;
  if (!data || typeof data.detail !== 'string') return null;
  return { detail: data.detail, code: String(data.code ?? ''), needs_replace: !!data.needs_replace };
}

function studentLabel(s: { first_name: string; last_name: string; username: string }): string {
  return personName(s) || s.username;
}

const SL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

interface StudentPickerProps {
  gradeId: number | null;
  currentStudent: { id: number; name: string } | null;
  onSelect: (studentId: number) => void;
  disabled?: boolean;
}

/** Search-by-name student picker, scoped to the exam's grade (plan B Task 6). */
function StudentPicker({ gradeId, currentStudent, onSelect, disabled = false }: StudentPickerProps) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const studentsQ = useStudentsList({ grade: gradeId ?? undefined }, { enabled: gradeId != null });
  const candidates = studentsQ.data?.results ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((s: StudentListRow) => studentLabel(s).toLowerCase().includes(q));
  }, [candidates, query]);

  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-ink">
        {currentStudent ? currentStudent.name : <span className="text-accent-ink">No student matched, pick one</span>}
      </p>
      <input
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search students by name…"
        aria-label="Search students by name"
        className="min-h-[40px] w-full rounded-[12px] border border-line-strong bg-surface px-3 py-2 text-sm text-ink outline-none transition focus:border-accent"
      />
      <div className="max-h-40 overflow-y-auto rounded-[12px] border border-line">
        {studentsQ.isLoading ? (
          <div className="flex justify-center py-3"><Spinner size="sm" /></div>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-2 text-xs text-muted">No students match.</p>
        ) : (
          (showAll ? filtered : filtered.slice(0, STUDENT_PREVIEW)).map((s: StudentListRow) => {
            const selected = s.id === currentStudent?.id;
            return (
              <button
                key={s.id}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(s.id)}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm disabled:opacity-50 ${
                  selected ? 'bg-accent-bg text-ink' : 'text-ink hover:bg-surface-2'
                }`}>
                <span>{studentLabel(s)}</span>
                {selected && <span className="rounded-full bg-mist px-2 py-0.5 text-xs font-semibold text-primary">Current</span>}
              </button>
            );
          })
        )}
      </div>
      {filtered.length > STUDENT_PREVIEW && (
        <ShowMoreFooter shown={showAll ? filtered.length : STUDENT_PREVIEW} total={filtered.length} expanded={showAll} onToggle={() => setShowAll((v) => !v)} />
      )}
    </div>
  );
}

interface BannerProps { tone: 'danger' | 'warning' | 'success'; title: string; children: React.ReactNode }
const BANNER = { danger: 'bg-danger-bg text-danger-ink', warning: 'bg-warning-bg text-warning-ink', success: 'bg-success-bg text-success' } as const;
function Banner({ tone, title, children }: BannerProps) {
  return (
    <div className={`rounded-[14px] p-3.5 ${BANNER[tone]}`}>
      <strong className="block">{title}</strong>
      <span className="text-[13px]">{children}</span>
    </div>
  );
}

interface Props {
  examId: number;
  /** The row being reviewed; null renders nothing. Only its id is used — the full
   * detail (geometry, answers, teacher questions...) is fetched fresh. */
  scan: SheetRow | null;
  /** The exam's grade, for the student picker's scope; null while the exam's
   * legacy detail hasn't loaded yet. */
  gradeId: number | null;
  /** Called when the sheet is done with: confirmed or discarded (the workspace moves to the next one). */
  onClose: () => void;
  /** Every sheet of the exam, to name the sheet a duplicate collides with. */
  allScans?: SheetRow[];
  /** The review queue, shown beside the sheet. */
  queue?: React.ReactNode;
}

/** The inline review pane (plan B Task 6, spec §7; design "Review sheets"): the
 * deskewed preview with a read-bubble overlay in one column, and the editor in
 * the next: warnings, roll number read, student, rows to check, teacher-graded
 * scores and Re-read / Discard / Confirm & next. Lays out the whole workspace:
 * the action bar, the queue beside the sheet, and the student / written answers below. */
/** Question text without its stored HTML, on one line. */
function plainText(html: string): string {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

export default function ScanDrawer({ examId, scan, gradeId, onClose, allScans, queue }: Props) {
  const scanId = scan?.id ?? null;

  const detailQ = useSheet(examId, scanId);
  const patchMut = useSheetPatch(examId);
  const confirmMut = useSheetConfirm(examId);
  const rereadMut = useSheetReread(examId);
  const writtenMut = useSheetsWritten(examId);
  const [writtenSwitch, setWrittenSwitch] = useState<'marked' | 'excluded' | null>(null);
  const detail = detailQ.data;

  const { objectUrl: previewUrl } = useBlobUrl(detail?.preview_url ?? detail?.image_url ?? null);
  // Setting the corners by hand happens on the original photo.
  const [cornerMode, setCornerMode] = useState(false);
  const { objectUrl: photoUrl } = useBlobUrl(cornerMode ? (detail?.image_url ?? null) : null);

  const [answers, setAnswers] = useState<Record<string, SheetAnswer | null>>({});
  const [teacherScores, setTeacherScores] = useState<Record<string, number>>({});
  const [confirmConflict, setConfirmConflict] = useState<SheetConfirmConflict | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fixed, setFixed] = useState<Set<number>>(new Set());
  const [showAllRows, setShowAllRows] = useState(false);
  const [zoom, setZoom] = useState<(typeof ZOOMS)[number]>(1);
  const viewerRef = useRef<HTMLDivElement | null>(null);

  // Reseed local edit state whenever a different sheet is opened, or a
  // re-read replaces the server's answers/confidence under us — never on the
  // idle poll for a sheet that's already settled.
  const seededRef = useRef<number | null>(null);
  const wasBusy = useRef(false);
  useEffect(() => {
    if (!detail) return;
    // A re-read replaces the server's answers: reseed when the sheet comes back from the reader.
    const nowBusy = detail.status === 'queued' || detail.status === 'reading';
    const back = wasBusy.current && !nowBusy;
    wasBusy.current = nowBusy;
    if (seededRef.current !== detail.id || back) {
      seededRef.current = detail.id;
      setAnswers({ ...detail.answers });
      setTeacherScores({ ...detail.teacher_scores });
      setConfirmConflict(null);
      setActionError(null);
      setFixed(new Set());
      setShowAllRows(false);
      setCornerMode(false);
    }
  }, [detail]);

  useEffect(() => {
    if (scanId == null) seededRef.current = null;
  }, [scanId]);

  const answersTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingAnswers = useRef<Record<string, SheetAnswer | null> | null>(null);
  useEffect(() => () => {
    if (answersTimer.current) clearTimeout(answersTimer.current);
  }, []);

  function flushAnswersNow() {
    if (answersTimer.current) clearTimeout(answersTimer.current);
    answersTimer.current = null;
    if (scanId != null && pendingAnswers.current) {
      patchMut.mutate({ scanId, body: { answers: pendingAnswers.current } });
    }
    pendingAnswers.current = null;
  }

  function handleAnswerChange(row: number, letter: SheetAnswer | null) {
    setFixed((prev) => new Set(prev).add(row));
    setAnswers((prev) => {
      const next = { ...prev, [String(row)]: letter };
      pendingAnswers.current = { ...(pendingAnswers.current ?? {}), [String(row)]: letter };
      if (answersTimer.current) clearTimeout(answersTimer.current);
      answersTimer.current = setTimeout(flushAnswersNow, ANSWERS_DEBOUNCE_MS);
      return next;
    });
  }

  function handleStudentSelect(studentId: number) {
    if (scanId == null) return;
    patchMut.mutate({ scanId, body: { student_id: studentId } });
  }

  // Marking a written question saves at once (one PATCH for "Mark the rest right").
  function setTeacherScoresNow(scores: Record<string, number>) {
    if (scanId == null) return;
    setTeacherScores((prev) => ({ ...prev, ...scores }));
    patchMut.mutate({ scanId, body: { teacher_scores: scores } });
  }

  async function handleConfirm(replace = false) {
    if (scanId == null) return;
    setActionError(null);
    flushAnswersNow();
    try {
      await confirmMut.mutateAsync({ scanId, replace });
      setConfirmConflict(null);
      onClose();
    } catch (err) {
      const conflict = extractConflict(err);
      if (conflict?.needs_replace) {
        setConfirmConflict(conflict);
      } else if (conflict) {
        setActionError(conflict.detail);
      } else {
        setActionError(apiErrorText(err, 'Could not confirm this sheet. Try again.'));
      }
    }
  }

  function handleDiscard() {
    if (scanId == null) return;
    patchMut.mutate({ scanId, body: { discard: true } }, { onSuccess: () => onClose() });
    setConfirmingDiscard(false);
  }

  function readWithCorners(corners: [number, number][]) {
    if (scanId == null) return;
    setActionError(null);
    if (answersTimer.current) clearTimeout(answersTimer.current);
    answersTimer.current = null;
    pendingAnswers.current = null;
    rereadMut.mutate({ scanId, corners }, {
      onSuccess: () => setCornerMode(false),
      onError: (err) => setActionError(apiErrorText(err, 'Could not read the sheet with these corners. Try again.')),
    });
  }

  function handleReread() {
    if (scanId == null) return;
    setActionError(null);
    // Re-reading replaces the answers: drop an unsent debounced edit so it cannot PATCH onto the queued sheet.
    if (answersTimer.current) clearTimeout(answersTimer.current);
    answersTimer.current = null;
    pendingAnswers.current = null;
    rereadMut.mutate(scanId);
  }

  const flagged = useMemo(
    () => (detail ? flaggedRows(detail.warnings, detail.row_confidence, detail.n_rows, LOW_CONFIDENCE) : new Map<number, 'multi' | 'low'>()),
    [detail],
  );
  const multiRows = useMemo(() => new Set([...flagged].filter(([, k]) => k === 'multi').map(([r]) => r)), [flagged]);
  const busy = (detail?.status === 'queued' || detail?.status === 'reading') || scan?.status === 'queued' || scan?.status === 'reading';
  // From the click on Re-read (or "Read with these corners") until the new reading is back.
  const rereading = busy || rereadMut.isPending;
  const canEdit = !!detail && !busy && detail.status !== 'confirmed' && detail.status !== 'discarded';
  const canSetCorners = canEdit;

  if (scanId == null) return null;

  const rowsToCheck = [...flagged.keys()].sort((a, b) => a - b);
  // A row's printed question number (row 1 is Q11 when Q1-10 are written answers).
  const qNum = (row: number) => detail?.row_numbers?.[String(row)] ?? row;
  const resolved = rowsToCheck.filter((r) => fixed.has(r)).length;
  const twin = detail?.duplicate_of?.[0];
  const twinRow = twin != null ? allScans?.find((s) => s.id === twin) : undefined;

  // After a fix, bring the next unfixed row to check into view and focus its bubbles.
  function pick(row: number, letter: SheetAnswer | null) {
    handleAnswerChange(row, letter);
    const next = rowsToCheck.find((r) => r !== row && !fixed.has(r));
    if (next != null) window.setTimeout(() => goToRow(next), 0);
  }

  function goToRow(row: number) {
    const el = viewerRef.current?.querySelector<HTMLElement>(`[data-row="${row}"]`);
    const target = el?.querySelector<HTMLElement>('button:not([disabled])');
    target?.scrollIntoView?.({ block: 'center', inline: 'nearest' });
    target?.focus({ preventScroll: true });
  }

  const studentName = detail?.student?.name ?? null;
  const allFixed = rowsToCheck.length > 0 && resolved === rowsToCheck.length;

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Action bar: who this sheet is, how far the check is, and what to do next. */}
      <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-[14px] border border-line bg-surface/95 px-4 py-3 shadow-card backdrop-blur">
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold text-ink">
            {studentName ?? <span className="text-accent-ink">No student matched yet</span>}
          </p>
          <p className="font-mono text-xs text-muted">
            Sheet #{scanId} · roll {detail?.roll_number_read != null ? formatRoll(detail.roll_number_read) : 'not read'}
          </p>
        </div>
        {rereading && (
          <span role="status" className="inline-flex items-center gap-1.5 rounded-full bg-mist px-2.5 py-1 text-xs font-semibold text-muted-2">
            <Spinner size="sm" /> Re-reading…
          </span>
        )}
        {detail && rowsToCheck.length > 0 && (
          <div className="min-w-[160px] flex-1">
            <p className={`text-xs font-semibold ${allFixed ? 'text-success' : 'text-accent-ink'}`}>
              {allFixed ? `All ${rowsToCheck.length} rows checked` : `${rowsToCheck.length - resolved} ${rowsToCheck.length - resolved === 1 ? 'row' : 'rows'} to check on the sheet`}
            </p>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-track">
              <div className="h-full rounded-full bg-success transition-all" style={{ width: `${(resolved / rowsToCheck.length) * 100}%` }} />
            </div>
          </div>
        )}
        {detail && rowsToCheck.length === 0 && <span className="flex-1" />}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" loading={rereadMut.isPending} disabled={!canEdit} onClick={handleReread}>
            Re-read
          </Button>
          <Button variant="danger-outline" size="sm" disabled={!canEdit} onClick={() => setConfirmingDiscard(true)}>
            Discard
          </Button>
          <Button
            loading={confirmMut.isPending} disabled={!canEdit}
            icon={<ArrowRight size={18} strokeWidth={1.8} aria-hidden />} className="flex-row-reverse"
            onClick={() => handleConfirm(false)}>
            Confirm &amp; next
          </Button>
        </div>
      </div>

      {detail && (detail.warnings.length > 0 || detail.status === 'error' || detail.status === 'duplicate' || actionError) && (
        <div className="flex flex-col gap-2.5">
          {detail.status === 'error' && !cornerMode && !rereading && (
            <Banner tone="danger" title={detail.warnings.includes('no_fiducials') ? 'Corner marks not found' : 'This sheet could not be read'}>
              {detail.warnings.includes('no_fiducials')
                ? 'The corner squares could not be found on the photo. Click them yourself to read it, or re-photograph it flat with all four corner squares visible.'
                : (detail.error || 'Re-read it, or re-photograph it flat with all four corner squares visible.')}
              {detail.warnings.includes('no_fiducials') && detail.image_url && canSetCorners && (
                <span className="mt-2 block">
                  <Button size="sm" onClick={() => setCornerMode(true)}>Set the corners by hand</Button>
                </span>
              )}
            </Banner>
          )}
          {detail.status === 'duplicate' && (
            <Banner
              tone="warning"
              title={twin != null
                ? `${detail.roll_number_read != null ? `Roll ${formatRoll(detail.roll_number_read)}` : 'This student'} is also on sheet #${twin}`
                : 'Another sheet already matched this student'}>
              {twin != null && detail.student
                ? `Sheet #${twin} is ${twinRow?.status === 'confirmed' ? 'already confirmed' : 'also matched'} for ${detail.student.name}. `
                : ''}
              Keep the clearer one and discard the other, or pick the right student.
            </Banner>
          )}
          {detail.warnings.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {detail.warnings.map((w) => (
                <span key={w} className="inline-flex items-center whitespace-nowrap rounded-[8px] bg-warning-bg px-[9px] py-[3px] text-xs font-medium text-warning-ink">{warningLabel(w)}</span>
              ))}
            </div>
          )}
          {actionError && <p className="rounded-[12px] bg-danger-bg p-3 text-sm text-danger-ink">{actionError}</p>}
        </div>
      )}

      {detail && detail.teacher_questions.length > 0 && (() => {
        const nWritten = detail.teacher_questions.length;
        const mode = detail.exam.paper_written === 'excluded' ? 'excluded' : 'marked';
        return (
          // For the whole exam: the scanner reads only the bubbles, so the teacher decides
          // whether the written questions are marked by hand or left out of every score.
          <section aria-label="Written questions" className="rounded-[16px] border border-accent-line bg-accent-bg/60 p-4">
            <p className="text-[15px] font-semibold text-ink">
              This exam has {nWritten} written {nWritten === 1 ? 'question' : 'questions'}. How should {nWritten === 1 ? 'it' : 'they'} count?
            </p>
            <p className="mb-3 mt-0.5 text-[13px] text-muted-2">
              The scanner reads only the multiple-choice bubbles. This applies to every sheet of this exam{writtenMut.isPending ? ' (saving…)' : ''}.
            </p>
            <RadioCards<'marked' | 'excluded'>
              label="How the written questions count"
              className="sm:flex-row [&>button]:flex-1"
              value={mode}
              onChange={(k) => { if (k !== mode && canEdit && !writtenMut.isPending) setWrittenSwitch(k); }}
              options={[
                { key: 'marked', label: 'Mark them on each sheet', hint: 'Right / Half / Wrong for each student, below the sheet. Unmarked ones count as wrong.' },
                { key: 'excluded', label: 'Leave them out of the score', hint: `Every sheet is scored over its ${detail.n_rows} multiple-choice questions only.` },
              ]}
            />
          </section>
        );
      })()}

      <div className="grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-[240px_minmax(0,1fr)]">
        <div className="min-w-0">{queue}</div>

        {/* The sheet, where the rows to check are fixed (or the photo, to click its corners). */}
        {cornerMode && detail ? (
          <div className="min-w-0">
            {photoUrl ? (
              <CornerPicker imageUrl={photoUrl} initial={detail.manual_corners} busy={rereadMut.isPending}
                onSubmit={readWithCorners} onCancel={() => setCornerMode(false)} />
            ) : (
              <div className="flex h-64 items-center justify-center"><Spinner size="md" /></div>
            )}
          </div>
        ) : (
        <div className="min-w-0">
          {detail && rowsToCheck.length > 0 && (
            <div className="mb-2.5 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-muted">{resolved} of {rowsToCheck.length} fixed</span>
              {rowsToCheck.map((q) => {
                const isFixed = fixed.has(q);
                const cur = answers[String(q)] ?? null;
                const kind = flagged.get(q);
                return (
                  <button key={q} type="button" onClick={() => goToRow(q)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                      isFixed ? 'border-success-line bg-success-soft text-success' : kind === 'multi' ? 'border-danger-line bg-danger-bg text-danger-ink' : 'border-accent-line bg-accent-bg text-accent-ink'}`}>
                    <span className="font-mono">Q{qNum(q)}</span>{' '}
                    <span className="font-normal">{isFixed ? (cur === '*' ? 'invalid' : cur ? `set to ${cur}` : 'blank') : kind === 'multi' ? 'marked twice' : 'faint mark'}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted">
              {detail?.status === 'error' ? 'The photo as uploaded.' : rowsToCheck.length > 0 ? 'Click the right bubble on the sheet, or Blank. Any other bubble can be clicked to fix a misread.' : 'Click any bubble on the sheet to fix a misread.'}
            </p>
            <div role="group" aria-label="Zoom" className="inline-flex rounded-[10px] border border-line-strong bg-surface p-0.5">
              {ZOOMS.map((z) => (
                <button key={z} type="button" aria-pressed={zoom === z} onClick={() => setZoom(z)}
                  className={`rounded-[8px] px-2.5 py-1 font-mono text-xs font-semibold ${zoom === z ? 'bg-navy text-white' : 'text-muted-2 hover:text-ink'}`}>
                  {z === 1 ? 'Fit' : `${z * 100}%`}
                </button>
              ))}
            </div>
          </div>
          {/* Extra room on the right: the last column's Blank / Invalid buttons sit past the sheet's edge. */}
          <div className="relative">
          {rereading && (
            <div className="absolute inset-0 z-[5] grid place-items-center rounded-[16px] bg-surface/70 backdrop-blur-[1px]">
              <div className="flex flex-col items-center gap-2 rounded-[14px] bg-surface px-5 py-4 text-center shadow-card">
                <Spinner size="md" />
                <p className="text-sm font-semibold text-ink">Reading the sheet again…</p>
                <p className="text-xs text-muted">The marks show here when it is done.</p>
              </div>
            </div>
          )}
          <div ref={viewerRef} aria-busy={rereading || undefined} className="max-h-[78vh] overflow-auto rounded-[16px] bg-track py-3 pl-3 pr-[88px]">
            <div className="relative mx-auto rounded-[6px] bg-surface" style={{ width: `${zoom * 100}%`, maxWidth: zoom === 1 ? 760 : undefined }}>
              {!detail ? (
                <div className="flex h-64 items-center justify-center"><Spinner size="md" /></div>
              ) : (
                <>
                  {previewUrl ? (
                    <img src={previewUrl} alt="Deskewed answer sheet" className="block w-full rounded-[6px]" />
                  ) : (
                    // No picture yet (or it failed to load): the bubbles still sit on a sheet-shaped page.
                    <div className="grid w-full place-items-center text-xs text-muted"
                      style={{ aspectRatio: `${detail.geometry.width || 1} / ${detail.geometry.height || 1.414}` }}>
                      {detail.image_url || detail.preview_url ? <Spinner size="md" /> : 'No picture of this sheet'}
                    </div>
                  )}
                  {/* An unread sheet has no straightened frame: its photo is shown as is. */}
                  {detail.status !== 'error' && (
                    <SheetOverlay detail={detail} answers={answers} flagged={flagged} fixed={fixed} canEdit={canEdit} qNum={qNum} onPick={pick} />
                  )}
                </>
              )}
            </div>
          </div>
          </div>
          <div className="mt-2.5 flex flex-wrap gap-3.5 text-xs text-muted">
            <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-full bg-success" />Read / fixed</span>
            <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-full bg-warning" />Faint mark</span>
            <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-full bg-danger" />Marked twice</span>
            {detail?.image_url && canSetCorners && !(detail.status === 'error' && detail.warnings.includes('no_fiducials')) && (
              <TextLinkButton onClick={() => setCornerMode(true)} className="ml-auto">
                {detail.manual_corners ? 'Adjust the corners' : 'Set the corners by hand'}
              </TextLinkButton>
            )}
          </div>
        </div>
        )}
      </div>

      {detailQ.isError ? (
        <ErrorPanel onRetry={() => detailQ.refetch()} />
      ) : detail ? (
        <div className="grid grid-cols-1 gap-[18px] border-t border-hairline pt-[18px] min-[981px]:grid-cols-2">
          <div className="min-w-0">
            <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[110px_minmax(0,1fr)]">
              <div>
                <p className={SL}><Term k="roll_number">Roll no.</Term> read</p>
                <p className="mt-1.5 font-mono text-[22px] font-semibold text-ink">
                  {formatRoll(detail.roll_number_read)}
                </p>
              </div>
              <div>
                <p className={`${SL} mb-1.5`}>Student</p>
                <StudentPicker
                  gradeId={gradeId}
                  currentStudent={detail.student}
                  onSelect={handleStudentSelect}
                  disabled={!canEdit || patchMut.isPending}
                />
              </div>
            </div>
            {detail.status === 'needs_review' && rowsToCheck.length === 0 && (
              <div className="mt-3">
                <Banner tone="success" title={`All ${detail.n_rows} bubble rows read cleanly`}>
                  Only the student needs checking on this sheet.
                </Banner>
              </div>
            )}
          </div>

          <div className="min-w-0">
            {detail.teacher_questions.length > 0 && (() => {
              const marked = detail.teacher_questions.filter((q) => teacherScores[String(q.id)] != null).length;
              const unmarked = detail.teacher_questions.filter((q) => teacherScores[String(q.id)] == null);
              const excluded = detail.exam.paper_written === 'excluded';
              const nWritten = detail.teacher_questions.length;
              return (
              <div className="mb-4">
                <span className={SL}><Term k="graded_by_teacher">Graded by teacher</Term></span>
                {excluded ? (
                  <p className="mt-2 rounded-[12px] bg-surface-2 px-3.5 py-2.5 text-[13px] text-muted-2">
                    The {nWritten} written {nWritten === 1 ? 'question is' : 'questions are'} left out: every sheet of this exam is scored over its {detail.n_rows} multiple-choice questions.
                  </p>
                ) : (<>
                <div className="mt-2 flex flex-wrap items-baseline justify-end gap-2">
                  <span className="flex items-baseline gap-3">
                    <span className={`font-mono text-xs ${marked === detail.teacher_questions.length ? 'text-success' : 'text-muted'}`}>
                      {marked} of {detail.teacher_questions.length} marked
                    </span>
                    {unmarked.length > 0 && canEdit && (
                      <TextLinkButton onClick={() => setTeacherScoresNow(Object.fromEntries(unmarked.map((q) => [String(q.id), q.max_score])))}>
                        Mark the rest right
                      </TextLinkButton>
                    )}
                  </span>
                </div>
                {/* One full-width row per question: Right / Half / Wrong in one click each. */}
                <div className="mt-2 flex flex-col gap-2">
                  {detail.teacher_questions.map((q) => {
                    const text = plainText(q.text);
                    const label = q.number != null ? `Q${q.number}` : null;
                    const cur = teacherScores[String(q.id)];
                    const name = label ?? text;
                    const choices = [
                      { key: 'right', text: 'Right', score: q.max_score, on: 'border-success bg-success text-white' },
                      { key: 'half', text: 'Half', score: q.max_score / 2, on: 'border-warning bg-warning text-white' },
                      { key: 'wrong', text: 'Wrong', score: 0, on: 'border-danger bg-danger text-white' },
                    ];
                    return (
                    <div key={q.id} className={`flex min-h-[44px] w-full min-w-0 items-center gap-2.5 rounded-[12px] border px-3.5 py-1.5 ${
                      cur == null ? 'border-accent-line bg-accent-bg/50' : 'border-line-strong bg-surface'}`}>
                      {label && <span className="min-w-[30px] font-mono text-xs font-semibold text-muted">{label}</span>}
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink" title={text}>{text}</span>
                      <span role="radiogroup" aria-label={`Mark ${name}`} className="flex flex-none gap-1">
                        {choices.map((c) => {
                          const selected = cur != null && cur === c.score;
                          return (
                            <button key={c.key} type="button" role="radio" aria-checked={selected} disabled={!canEdit}
                              aria-label={`${c.text}, ${name}`}
                              onClick={() => setTeacherScoresNow({ [String(q.id)]: c.score })}
                              className={`rounded-full border-[1.5px] px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                selected ? c.on : 'border-control bg-surface text-muted-2 hover:border-ink hover:text-ink'}`}>
                              {c.text}
                            </button>
                          );
                        })}
                      </span>
                    </div>
                    );
                  })}
                </div>
                </>)}
              </div>
              );
            })()}
            <TextLinkButton aria-expanded={showAllRows} onClick={() => setShowAllRows((v) => !v)} className="-ml-2">
              {showAllRows ? 'Hide all rows' : `Show all ${detail.n_rows} rows`}
            </TextLinkButton>
            {showAllRows && (
              <div className="mt-1">
                <AnswerStrip
                  nRows={detail.n_rows}
                  answers={answers}
                  rowConfidence={detail.row_confidence}
                  multiRows={multiRows}
                  onChange={handleAnswerChange}
                  disabled={!canEdit}
                  rowNumbers={detail.row_numbers}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex justify-center py-10"><Spinner size="md" /></div>
      )}

      <ConfirmDialog
        isOpen={!!confirmConflict}
        title="Replace the existing paper result?"
        body={confirmConflict?.detail}
        confirmLabel="Replace"
        onConfirm={() => handleConfirm(true)}
        onCancel={() => setConfirmConflict(null)}
      />

      <ConfirmDialog
        isOpen={writtenSwitch != null}
        title={writtenSwitch === 'excluded' ? 'Leave the written questions out of the score?' : 'Mark the written questions?'}
        body={writtenSwitch === 'excluded'
          ? `Every sheet of this exam, including those already confirmed, is scored over its ${detail?.n_rows ?? ''} multiple-choice questions only. You can switch back later.`
          : 'The written questions count again on every sheet of this exam: mark them on each sheet. On sheets already confirmed, unmarked ones count as wrong.'}
        confirmLabel={writtenSwitch === 'excluded' ? 'Leave them out' : 'Mark them'}
        onConfirm={() => { if (writtenSwitch) writtenMut.mutate(writtenSwitch, { onSettled: () => setWrittenSwitch(null) }); }}
        onCancel={() => setWrittenSwitch(null)}
      />

      <ConfirmDialog
        isOpen={confirmingDiscard}
        title="Discard this sheet?"
        body="It will be left out of confirmation and results."
        confirmLabel="Discard"
        onConfirm={handleDiscard}
        onCancel={() => setConfirmingDiscard(false)}
      />
    </div>
  );
}
